import { supabase } from "@/integrations/supabase/client";
import {
  Bateria,
  CreateBateria,
  UpdateBateria,
  BateriaCategoriaArquitetural,
} from "@/types/baterias";

interface BateriaDB {
  id: string;
  marca: string;
  modelo: string;
  tipo_celula: string;
  categoria_arquitetural: string;
  tensao_nominal_v: number | string;
  capacidade_nominal_ah: number | string | null;
  energia_kwh: number | string;
  potencia_nominal_kw: number | string | null;
  corrente_max_carga_a: number | string | null;
  corrente_max_descarga_a: number | string | null;
  tensao_operacao_min_v: number | string | null;
  tensao_operacao_max_v: number | string | null;
  dimensoes_comprimento: number;
  dimensoes_largura: number;
  dimensoes_altura: number;
  peso_kg: number | string;
  ciclos_uteis_80_dod: number | null;
  garantia_anos: number;
  observacoes: string | null;
  imagem_url?: string | null;
  created_at: string;
  updated_at: string;
}

function toNum(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? n : null;
}

function mapRow(item: BateriaDB): Bateria {
  return {
    id: item.id,
    marca: item.marca || "",
    modelo: item.modelo || "",
    tipoCelula: item.tipo_celula || "",
    categoriaArquitetural: item.categoria_arquitetural as BateriaCategoriaArquitetural,
    tensaoNominalV: Number(toNum(item.tensao_nominal_v) ?? 0),
    capacidadeNominalAh: toNum(item.capacidade_nominal_ah),
    energiaKwh: Number(toNum(item.energia_kwh) ?? 0),
    potenciaNominalKw: toNum(item.potencia_nominal_kw),
    correnteMaxCargaA: toNum(item.corrente_max_carga_a),
    correnteMaxDescargaA: toNum(item.corrente_max_descarga_a),
    tensaoOperacaoMinV: toNum(item.tensao_operacao_min_v),
    tensaoOperacaoMaxV: toNum(item.tensao_operacao_max_v),
    dimensoes: {
      comprimento: item.dimensoes_comprimento || 0,
      largura: item.dimensoes_largura || 0,
      altura: item.dimensoes_altura || 0,
    },
    pesoKg: Number(toNum(item.peso_kg) ?? 0),
    ciclosUteis80Dod: item.ciclos_uteis_80_dod,
    garantiaAnos: item.garantia_anos ?? 0,
    observacoes: item.observacoes,
    imagemUrl: item.imagem_url ?? null,
    createdAt: item.created_at || "",
    updatedAt: item.updated_at || "",
  };
}

function mapCreateToRow(b: CreateBateria) {
  return {
    marca: b.marca,
    modelo: b.modelo,
    tipo_celula: b.tipoCelula,
    categoria_arquitetural: b.categoriaArquitetural,
    tensao_nominal_v: b.tensaoNominalV,
    capacidade_nominal_ah: b.capacidadeNominalAh,
    energia_kwh: b.energiaKwh,
    potencia_nominal_kw: b.potenciaNominalKw,
    corrente_max_carga_a: b.correnteMaxCargaA,
    corrente_max_descarga_a: b.correnteMaxDescargaA,
    tensao_operacao_min_v: b.tensaoOperacaoMinV,
    tensao_operacao_max_v: b.tensaoOperacaoMaxV,
    dimensoes_comprimento: b.dimensoes.comprimento,
    dimensoes_largura: b.dimensoes.largura,
    dimensoes_altura: b.dimensoes.altura,
    peso_kg: b.pesoKg,
    ciclos_uteis_80_dod: b.ciclosUteis80Dod,
    garantia_anos: b.garantiaAnos,
    observacoes: b.observacoes,
    imagem_url: b.imagemUrl ?? null,
  };
}

function buildUpdateRow(b: UpdateBateria): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  if (b.marca !== undefined) row.marca = b.marca;
  if (b.modelo !== undefined) row.modelo = b.modelo;
  if (b.tipoCelula !== undefined) row.tipo_celula = b.tipoCelula;
  if (b.categoriaArquitetural !== undefined) row.categoria_arquitetural = b.categoriaArquitetural;
  if (b.tensaoNominalV !== undefined) row.tensao_nominal_v = b.tensaoNominalV;
  if (b.capacidadeNominalAh !== undefined) row.capacidade_nominal_ah = b.capacidadeNominalAh;
  if (b.energiaKwh !== undefined) row.energia_kwh = b.energiaKwh;
  if (b.potenciaNominalKw !== undefined) row.potencia_nominal_kw = b.potenciaNominalKw;
  if (b.correnteMaxCargaA !== undefined) row.corrente_max_carga_a = b.correnteMaxCargaA;
  if (b.correnteMaxDescargaA !== undefined) row.corrente_max_descarga_a = b.correnteMaxDescargaA;
  if (b.tensaoOperacaoMinV !== undefined) row.tensao_operacao_min_v = b.tensaoOperacaoMinV;
  if (b.tensaoOperacaoMaxV !== undefined) row.tensao_operacao_max_v = b.tensaoOperacaoMaxV;
  if (b.dimensoes !== undefined) {
    row.dimensoes_comprimento = b.dimensoes.comprimento;
    row.dimensoes_largura = b.dimensoes.largura;
    row.dimensoes_altura = b.dimensoes.altura;
  }
  if (b.pesoKg !== undefined) row.peso_kg = b.pesoKg;
  if (b.ciclosUteis80Dod !== undefined) row.ciclos_uteis_80_dod = b.ciclosUteis80Dod;
  if (b.garantiaAnos !== undefined) row.garantia_anos = b.garantiaAnos;
  if (b.observacoes !== undefined) row.observacoes = b.observacoes;
  if (b.imagemUrl !== undefined) row.imagem_url = b.imagemUrl;
  return row;
}

export const bateriasService = {
  async uploadImagem(bateriaId: string, file: File): Promise<string> {
    const fileExt = file.name.split(".").pop();
    const fileName = `${bateriaId}_${Date.now()}.${fileExt}`;
    const filePath = `baterias/${fileName}`;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      throw new Error("Tipo de arquivo não permitido. Use JPEG, PNG, GIF ou WebP.");
    }

    const { error } = await supabase.storage.from("baterias").upload(filePath, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type,
    });

    if (error) {
      console.error("Erro no upload da imagem:", error);
      throw new Error(`Erro ao fazer upload da imagem: ${error.message}`);
    }

    const { data: urlData } = supabase.storage.from("baterias").getPublicUrl(filePath);
    return urlData.publicUrl;
  },

  async getAll(): Promise<Bateria[]> {
    const { data, error } = await supabase
      .from("baterias")
      .select("*")
      .order("marca", { ascending: true });

    if (error) {
      console.error("Erro ao buscar baterias:", error);
      throw new Error(`Erro ao buscar baterias: ${error.message}`);
    }

    return (data || []).map((row) => mapRow(row as BateriaDB));
  },

  async create(bateria: CreateBateria): Promise<Bateria> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error("Sessão não encontrada. Faça login novamente.");
    }

    const insertData = mapCreateToRow(bateria);

    const { data, error } = await supabase.from("baterias").insert(insertData).select().single();

    if (error) {
      console.error("Erro ao criar bateria:", error);
      throw new Error(error.message || "Erro ao criar bateria");
    }
    if (!data) throw new Error("Bateria criada mas dados não retornados");

    return mapRow(data as BateriaDB);
  },

  async update(id: string, bateria: UpdateBateria): Promise<Bateria> {
    const updateData = buildUpdateRow(bateria);

    const { data, error } = await supabase
      .from("baterias")
      .update(updateData)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("Erro ao atualizar bateria:", error);
      throw new Error(error.message || "Erro ao atualizar bateria");
    }
    if (!data) throw new Error("Bateria não encontrada para atualização");

    return mapRow(data as BateriaDB);
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase.from("baterias").delete().eq("id", id);
    if (error) {
      console.error("Erro ao excluir bateria:", error);
      throw new Error(error.message || "Erro ao excluir bateria");
    }
  },
};
